export function normalizeFacilitiesToArray(facilities: any): string[] {
  if (!facilities) return [];

  // Already an array
  if (Array.isArray(facilities)) {
    return facilities.map((f) => {
      if (typeof f === "string") return capitalizeFirst(f);
      return String(f);
    });
  }

  if (typeof facilities === "object") {
    return Object.entries(facilities)
      .filter(([_, enabled]) => enabled === true)
      .map(([key]) => capitalizeFirst(key));
  }

  return [];
}

export function normalizeRoomRevenue(roomRev: any): any {
  if (!roomRev) return null;

  let adr: number[] = [];
  let occ: number[] = [];

  // Handle template format: roomRev: { adr: [], occ: [] }
  if (roomRev.adr && roomRev.occ) {
    adr = Array.isArray(roomRev.adr) ? roomRev.adr : [];
    occ = Array.isArray(roomRev.occ) ? roomRev.occ : [];
  }
  // Handle project format: roomRevenue: { monthly: { adr, occ } }
  else if (roomRev.monthly) {
    adr = Array.isArray(roomRev.monthly.adr) ? roomRev.monthly.adr : [];
    occ = Array.isArray(roomRev.monthly.occ) ? roomRev.monthly.occ : [];
  }

  // Ensure 12 months
  const ensure12 = (arr: any[]) => {
    const nums = arr.map((v) => Number(v) || 0).slice(0, 12);
    while (nums.length < 12) nums.push(0);
    return nums;
  };

  adr = ensure12(adr);
  occ = ensure12(occ);

  // Check if we have valid data (at least some non-zero values)
  const hasData = adr.some((v) => v > 0) || occ.some((v) => v > 0);

  if (!hasData) return null;

  return {
    monthly: {
      adr,
      occ,
    },
  };
}

export function normalizeFnBRevenue(fnb: any): any {
  if (!fnb) return null;

  const mealPeriods = ["breakfast", "lunch", "dinner", "bar"];
  const monthly: any = {};

  mealPeriods.forEach((period) => {
    const guestCapture = fnb.capture?.[period] || 0;
    const avgCheck = fnb.check?.guest?.[period] || 0;
    const externalAvgCheck = fnb.check?.external?.[period] || 0;
    const externalCustomers = fnb.external?.perDay?.[period] || 0;

    monthly[period] = {
      avgCheck: Array(12).fill(Number(avgCheck)),
      externalAvgCheck: Array(12).fill(Number(externalAvgCheck)),
      guestsPerOccRoom: Array(12).fill(Number(guestCapture)),
      externalCustomers: Array(12).fill(Number(externalCustomers)),
    };
  });

  return {
    monthly,
  };
}

export function normalizeInvestmentBudget(budget: any): any {
  if (!budget) return null;

  return {
    netPurchasePrice: Number(budget.netPurchasePrice) || 0,
    reTransferTax: Number(budget.transferTax) || 0,
    dealCosts: Number(budget.dealCosts) || 0,
    constructionCosts: Number(budget.construction) || 0,
    ffeOse: Number(budget.ffoeOse) || 0,
    professionalFees: Number(budget.profFees) || 0,
    planningCharges: Number(budget.planningFees) || 0,
    developmentFee: Number(budget.devFee) || 0,
    insuranceAdmin: Number(budget.insuranceAdmin) || 0,
    otherDev: Number(budget.other) || 0,
    preOpening: Number(budget.preopening) || 0,
    contingencyPct: Number(budget.contingencyPct) || 10,
    grandTotal: 0, // Will be calculated
  };
}

export function normalizeTemplateKpisForProject(templateKpis: any): any {
  if (!templateKpis) return {};

  const normalized: any = {
    // Basic info
    dealName: templateKpis.dealName || "",
    location: templateKpis.location || "",
    address: templateKpis.address || "",
    propertyType: templateKpis.propertyType || "Boutique",
    starRating: templateKpis.starRating || 4,
    currency: templateKpis.currency || "EUR",
    gfaSqm: Number(templateKpis.gfaSqm) || 0,

    // Purchase price
    purchase: {
      price: Number(templateKpis.purchase?.price) || 0,
    },

    // Facilities
    facilities: normalizeFacilitiesToArray(templateKpis.facilities),

    // Room mix
    rooms: templateKpis.rooms?.total || 0,
    roomMix: templateKpis.roomMix || [],
  };

  // Room revenue
  const roomRevenue = normalizeRoomRevenue(
    templateKpis.roomRev || templateKpis.roomRevenue,
  );
  if (roomRevenue) {
    normalized.roomRevenue = roomRevenue;
  }

  // F&B revenue
  // const fbRevenue = normalizeFnBRevenue(
  //   templateKpis.fnb || templateKpis.fbRevenue
  // );
  // if (fbRevenue) {
  //   normalized.fbRevenue = fbRevenue;
  // }

  // Investment budget
  const investmentBudget = normalizeInvestmentBudget(templateKpis.budget);
  if (investmentBudget) {
    normalized.investmentBudget = investmentBudget;
  }

  // Operating expenses (copy as-is for now)
  if (templateKpis.opex) {
    normalized.opex = { ...templateKpis.opex };
  }

  // Other revenue (copy as-is)
  if (templateKpis.otherRev) {
    normalized.otherRevenue = { ...templateKpis.otherRev };
  }

  return normalized;
}

/**
 * Helper: Capitalize first letter
 */
function capitalizeFirst(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Check if room revenue data is valid (has any non-zero values)
 */
export function hasValidRoomRevenue(roomRev: any): boolean {
  if (!roomRev) return false;

  let adr: number[] = [];
  let occ: number[] = [];

  if (roomRev.adr && roomRev.occ) {
    adr = Array.isArray(roomRev.adr) ? roomRev.adr : [];
    occ = Array.isArray(roomRev.occ) ? roomRev.occ : [];
  } else if (roomRev.monthly) {
    adr = Array.isArray(roomRev.monthly.adr) ? roomRev.monthly.adr : [];
    occ = Array.isArray(roomRev.monthly.occ) ? roomRev.monthly.occ : [];
  }

  return adr.some((v) => Number(v) > 0) || occ.some((v) => Number(v) > 0);
}
